import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
    AlgoAction,
    ConvictionAllocationType,
    GrowthAllocationType,
    Portfolio,
    SecuredAllocationType,
    UnmanagedAllocationType,
} from '../../_openapi/portfolio-optimizer';
import { useOptimizationMeta } from '../../api/optimization';
import { AllocationType } from '../allocation_types';

const isValidAllocationType = (value: unknown): value is AllocationType => {
    return (
        Object.values(GrowthAllocationType).includes(value as GrowthAllocationType) ||
        Object.values(SecuredAllocationType).includes(value as SecuredAllocationType) ||
        Object.values(ConvictionAllocationType).includes(value as ConvictionAllocationType) ||
        Object.values(UnmanagedAllocationType).includes(value as UnmanagedAllocationType)
    );
};

interface UseOptimizationPrePopulationProps {
    portfoliosData?: { portfolios: Portfolio[] };
    setDataAsOfDate: (date: Date) => void;
    setTradeDateOverride: (date: Date | undefined) => void;
    setSelectedAlgoAction: (action: AlgoAction) => void;
    setSelectedAllocationTypes: (types: AllocationType[]) => void;
    setSelectedReason: (reason: string) => void;
    setIncludedPortfolios: (portfolios: Portfolio[]) => void;
    setExcludedPortfolios: (portfolios: Portfolio[]) => void;
}

export const useOptimizationPrePopulation = ({
    portfoliosData,
    setDataAsOfDate,
    setTradeDateOverride,
    setSelectedAlgoAction,
    setSelectedAllocationTypes,
    setSelectedReason,
    setIncludedPortfolios,
    setExcludedPortfolios,
}: UseOptimizationPrePopulationProps) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const rerunOptimizationId = searchParams.get('rerun');
    const stateGuids = (location.state as { portfolioGuids?: string[] })?.portfolioGuids;
    const paramGuids = searchParams.get('portfolioGuids')?.split(',').filter(Boolean);
    const portfolioGuidsFromState = stateGuids ?? paramGuids;

    const { data: rerunOptimizationData } = useOptimizationMeta(
        rerunOptimizationId ? parseInt(rerunOptimizationId, 10) : undefined,
    );

    const hasPrePopulatedFromRerun = useRef(false);
    const hasPrePopulatedFromGuids = useRef(false);

    // Pre-populate form when rerunning an optimization (only once)
    useEffect(() => {
        if (
            rerunOptimizationData?.optimizationRequest &&
            portfoliosData?.portfolios &&
            !hasPrePopulatedFromRerun.current
        ) {
            hasPrePopulatedFromRerun.current = true;
            const {
                dataAsOfDate: rerunDataAsOfDate,
                tradeDateOverride: rerunTradeDateOverride,
                algoAction,
                allocationTypes,
                accountGuids,
                details,
            } = rerunOptimizationData.optimizationRequest;

            if (rerunDataAsOfDate) {
                setDataAsOfDate(new Date(rerunDataAsOfDate));
            }
            if (rerunTradeDateOverride) {
                setTradeDateOverride(new Date(rerunTradeDateOverride));
            }
            if (algoAction) {
                setSelectedAlgoAction(algoAction);
            }
            if (allocationTypes) {
                const validAllocationTypes = allocationTypes.filter(isValidAllocationType);
                setSelectedAllocationTypes(validAllocationTypes);
            }
            if (details) {
                setSelectedReason(details);
            }

            // Set portfolio inclusions/exclusions based on accountGuids
            if (accountGuids && accountGuids.length > 0) {
                const includedPortfoliosList = portfoliosData.portfolios.filter((p) =>
                    accountGuids.includes(p.base.portfolioGuid ?? ''),
                );
                const excludedPortfoliosList = portfoliosData.portfolios.filter(
                    (p) => !accountGuids.includes(p.base.portfolioGuid ?? ''),
                );
                setIncludedPortfolios(includedPortfoliosList);
                setExcludedPortfolios(excludedPortfoliosList);
            } else if (accountGuids) {
                // Optimizations submitted before the account-GUID rollout carry no accountGuids to
                // restore portfolio selection from - the builder falls back to its default (all excluded).
                toast.warning(
                    'This optimization predates account GUID tracking, so its portfolio selection could not be restored. Please re-select portfolios.',
                );
            }
        }
    }, [
        rerunOptimizationData,
        portfoliosData?.portfolios,
        setDataAsOfDate,
        setTradeDateOverride,
        setSelectedAlgoAction,
        setSelectedAllocationTypes,
        setSelectedReason,
        setIncludedPortfolios,
        setExcludedPortfolios,
    ]);

    // Pre-populate from investment request portfolio GUIDs (only once)
    useEffect(() => {
        if (portfolioGuidsFromState && portfoliosData?.portfolios && !hasPrePopulatedFromGuids.current) {
            hasPrePopulatedFromGuids.current = true;
            const portfolioGuids = portfolioGuidsFromState;

            if (portfolioGuids.length > 0) {
                // Try to find matching portfolios from the existing portfolio data
                const matchedPortfolios = portfoliosData.portfolios.filter((p) =>
                    portfolioGuids.includes(p.base.portfolioGuid ?? ''),
                );

                // Check for unmatched GUIDs and show warning
                const matchedGuids = new Set(matchedPortfolios.map((p) => p.base.portfolioGuid));
                const unmatchedGuids = portfolioGuids.filter((guid: string) => !matchedGuids.has(guid));

                if (unmatchedGuids.length > 0) {
                    const uniqueUnmatchedCount = new Set(unmatchedGuids).size;
                    toast.warning(
                        `${uniqueUnmatchedCount} portfolio${uniqueUnmatchedCount > 1 ? 's' : ''} could not be found and ${uniqueUnmatchedCount > 1 ? 'were' : 'was'} skipped. ${matchedPortfolios.length} portfolio${matchedPortfolios.length > 1 ? 's' : ''} added to optimization.`,
                    );
                }

                // Only include portfolios that were successfully matched
                const allExcludedPortfolios = portfoliosData.portfolios.filter(
                    (p) => !portfolioGuids.includes(p.base.portfolioGuid ?? ''),
                );

                setIncludedPortfolios(matchedPortfolios);
                setExcludedPortfolios(allExcludedPortfolios);
            }
        }
    }, [portfolioGuidsFromState, portfoliosData?.portfolios, setIncludedPortfolios, setExcludedPortfolios]);
};
